/* eslint-disable no-unused-vars */
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { preloadRoute } from './Routes';
import { isPrerendering } from './utils/detectPrerender';
import { separateAdjacentText } from './utils/prerenderText';
import { captureFirstRender, firstRenderHazards } from './utils/firstRender';

// PostHog is not started here: the inline snippet in public/index.html starts it
// before this bundle has even downloaded (issue #174 was the landing $pageview
// being lost to init order; there is no init order left to get wrong).

const rootElement = document.getElementById('root');
// Auth0Provider is now only in App.js to avoid duplicate SDK clients
const app = <App />;

// While react-snap renders, record the chunks this page needed before its first
// render as <link rel="preload"> tags; react-snap saves them with the HTML, so a
// visitor fetches the page's code alongside main.js instead of after it.
// webpack deletes its own chunk <script> tags once they load, so react-snap's
// fixWebpackChunksIssue has nothing to convert. Taken before render on purpose:
// anything loaded after mount (the exit-intent modal) is not on the paint path.
const markRouteChunksForPreload = () => {
  performance.getEntriesByType('resource')
    .map((entry) => new URL(entry.name).pathname)
    .filter((path) => /^\/static\/js\/[^/]+\.chunk\.js$/.test(path))
    .forEach((path) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = path;
      document.head.appendChild(link);
    });
};

// Prerendered routes whose static HTML hydrates cleanly: react-snap saves them as
// React first rendered them (utils/firstRender), with text-node boundaries kept
// (utils/prerenderText). Opt a route in only after diffing its snapshot against
// its first client render at phone and desktop widths and checking it hydrates
// with no React errors: once hydration succeeds, React keeps the static DOM as
// it is. A trailing '/*' opts in everything below a path, not the path itself:
// '/learn/*' is the articles, and the /learn hub is not opted in.
const CLEAN_HYDRATION_ROUTES = ['/start', '/upgrade', '/', '/learn/*', '/nfl', '/ncaaf', '/nhl', '/mlb', '/quiz/bettor-type'];

const routeOf = (pathname) => pathname.replace(/\/+$/, '') || '/';
const hydratesCleanly = (pathname) => {
  const route = routeOf(pathname);
  return CLEAN_HYDRATION_ROUTES.some((entry) => (
    entry.endsWith('/*') ? route.startsWith(entry.slice(0, -1)) : route === entry
  ));
};

// Installed before the first render, which is what it records.
const restoreFirstRender = isPrerendering() && hydratesCleanly(window.location.pathname)
  ? captureFirstRender(rootElement)
  : null;

// Load the current page's chunk first. On a prerendered page a route that is
// still loading would suspend during hydration, and React would throw the static
// HTML away (#418/#423); elsewhere it saves a frame of spinner.
preloadRoute(window.location.pathname).then(() => {
  if (isPrerendering()) markRouteChunksForPreload();
  if (rootElement.hasChildNodes()) {
    hydrateRoot(rootElement, app);
  } else {
    createRoot(rootElement).render(app);
  }
});

// Routes that never loaded a web font. /start was served from
// app/templates/index.html, which has no Google Fonts link, so it has always
// rendered in the system sans-serif. Its prerendered copy is built from
// public/index.html, which does load Inter -- as a render-blocking stylesheet --
// so the snapshot drops those links and the page looks exactly as it did.
const SYSTEM_FONT_ROUTES = ['/start'];

// For react-snap, which calls this the moment before it serializes the page.
window.snapSaveState = () => {
  const route = routeOf(window.location.pathname);
  if (restoreFirstRender) {
    const hazards = restoreFirstRender()
      ? firstRenderHazards(rootElement)
      : ['React rendered nothing into #root during its first commit'];
    separateAdjacentText(rootElement);
    // scripts/check-prerender-coverage.js fails the build on this marker.
    if (hazards.length) {
      const marker = document.createElement('div');
      marker.setAttribute('data-prerender-hazard', hazards.join('; '));
      rootElement.appendChild(marker);
    }
  }
  if (SYSTEM_FONT_ROUTES.includes(route)) {
    document.head.querySelectorAll('link[href*="fonts.googleapis.com"], link[href*="fonts.gstatic.com"]')
      .forEach((link) => link.remove());
  }
  return {
    __PRELOADED_STATE__: {}  // Add your state here if needed
  };
};
